import { CabButton } from "@CabComponents/CabButton";
import { Box, styled, Typography } from "@mui/material";
import { ReactNode } from "react";
import colors from "../../colors";
import UpgradeOverlay from '../../components/Common/UpgradeOverlay/UpgradeOverlay';
import CabSpinner from "@CabComponents/CabSpinner";


export interface UpdatedWidgetProps {
  title: string;
  buttonName?: string;
  buttonOnClick?: () => void;
  isLoading: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
  accessBlocked?: boolean;
  upgradeMessage?: string;
  allLoaded: boolean;
  borderColorOverride?: string;
}

const WidgetContainer = styled(Box, {label: "WidgetContainer"})({
  border: '1px solid',
  backgroundColor: colors.white900,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: 6,
  padding: '16px 0px 16px 16px',
  width: '100%',
  height: '100%',
});

const HeaderBox = styled(Box, {label: "HeaderBox"})({
  display: 'flex',
  justifyContent: 'space-between',
  paddingRight: 16,
  width: '100%',
  alignItems: 'center',
  flexShrink: 0
});

const ContentBox = styled(Box, {label: "ContentBox"})({
  backgroundColor: colors.white900,
  marginTop: 24,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  '::-webkit-scrollbar': {
    width: 8,
    height: 8,
    backgroundColor: colors.white900,
  },
  '::-webkit-scrollbar-thumb': {
    background: colors.black300,
    borderRadius: 4,
  }
});

const UpdatedDashboardWidget = ({
  title, buttonName, buttonOnClick, isLoading, emptyMessage, children, isEmpty, accessBlocked, upgradeMessage,
  allLoaded, borderColorOverride
}:
UpdatedWidgetProps): ReactNode => {
  return (
    <WidgetContainer sx={{borderColor: borderColorOverride ?? colors.black400}}>
      <HeaderBox>
        <Typography variant='h4'>{title}</Typography>
        {buttonName && buttonOnClick && (
          <CabButton buttonType='text' sx={{ fontSize: '14px', color: colors.lavendar800 }} onClick={buttonOnClick}>
            {buttonName}
          </CabButton>
        )}
      </HeaderBox>
      <ContentBox sx={{ backgroundColor: colors.white900 }}>
        {allLoaded && !isEmpty && !isLoading && children}
        {isEmpty && allLoaded && !isLoading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <Typography
              variant='body1'
              sx={{
                padding: '10px',
                textAlign: 'center'
              }}>{emptyMessage}</Typography>
          </Box>
        )}
        {(isLoading || !allLoaded) && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <CabSpinner scale={4}/>
          </Box>
        )}
      </ContentBox>
      {accessBlocked && (
        <UpgradeOverlay
          show={accessBlocked}
          message={upgradeMessage} />
      )}
    </WidgetContainer>
  );
};

export default UpdatedDashboardWidget;